import { FC, useCallback } from 'react'
import { useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { useShallow } from 'zustand/react/shallow'

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { MenuItem } from '@mui/material'
import { DataCard } from '@/components/card'
import { DOWNLOAD_CSV_LABEL } from '@/components/DownloadCsv/DownloadCsv'
import { downloadBlobAsFile } from '@/components/DownloadCsv/utils'
import { DropdownMenu } from '@/components/DropdownMenu'
import { ActionsMenu } from '@/components/table/actions'
import { ActionReportIssues } from '@/components/table/actions/ActionReportIssues/ActionReportIssues'
import { ActionReportNoIssues } from '@/components/table/actions/ActionReportNoIssues/ActionReportNoIssues'
import { FilterableTable } from '@/components/table/base'
import { separateALC } from '@/components/table/filters/DateRangeFilter/utils'
import { STORE_STATUS } from '@/constants/store'
import { streamingRestApi } from '@/service/streamingApi'
import { usePastEventsStore } from '@/stores/pastEvents/pastEvents.store'

import { PAST_EVENTS_COLUMNS, pastEventsFilters } from './pastEvents.table'

export const DOWNLOAD_CSV_BUTTON_TEST_ID = 'downloadCsv-button' as const
export const DOWNLOAD_CSV_BY_US_STATE_BUTTON_TEST_ID = 'downloadCsv-button' as const

const TABLE_ID = 'Past Events table' as const
const NO_DATA_TEXT = 'Past Events table is empty' as const

const PAST_EVENTS_BY_US_STATE_CSV_FILE_NAME = 'past-events-by-us-state-report.csv' as const
const PAST_EVENTS_CSV_FILE_NAME = 'past-events-report.csv' as const

const DOWNLOAD_CSV_REPORT_BY_US_STATE_LABEL = 'Download Report by U.S. State' as const
const DOWNLOAD_CSV_REPORT_LABEL = 'Download Report' as const

const PastEventsView: FC = () => {
  const { enqueueSnackbar } = useSnackbar()

  const {
    data,
    pagination,
    selectedFilters,
    isFetching,
    isLoading,
    fetchData,
    setFilters,
    setPagination,
    resetState
  } = usePastEventsStore(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      pagination: state.pagination,
      setPagination: state.setPagination,
      selectedFilters: state.filters,
      setFilters: state.setFilters,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching,
      resetState: state.resetState
    }))
  )

  const dateRangeValue = selectedFilters.dateRange?.[0].value?.toString() ?? ''

  const handleDownloadPastEventsByUSStateCsv = useCallback(async () => {
    const [fromDate, toDate] = separateALC(dateRangeValue)

    const params = {
      countryCode: 'US',
      fromDate,
      toDate
    }

    try {
      const data = await streamingRestApi.endpoints.analytics.downloadPastEventsByUSStateCsv(params)
      downloadBlobAsFile(data.data, PAST_EVENTS_BY_US_STATE_CSV_FILE_NAME)
    } catch (error) {
      enqueueSnackbar('Failed to download the report. Please try again later.', {
        variant: 'error'
      })
    }
  }, [selectedFilters.dateRange, dateRangeValue])

  const handleDownloadPastEventsCSV = useCallback(async () => {
    const [fromDate, toDate] = separateALC(dateRangeValue)

    const params = {
      fromDate,
      toDate
    }

    try {
      const data = await streamingRestApi.endpoints.analytics.downloadPastEventsCsv(params)
      downloadBlobAsFile(data.data, PAST_EVENTS_CSV_FILE_NAME)
    } catch (error) {
      enqueueSnackbar('Failed to download the report. Please try again later.', {
        variant: 'error'
      })
    }
  }, [selectedFilters.dateRange, dateRangeValue])

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  return (
    <DataCard
      title="Past Events"
      headerPostfixActions={
        <DropdownMenu label={DOWNLOAD_CSV_LABEL}>
          <MenuItem data-testid={DOWNLOAD_CSV_BUTTON_TEST_ID} onClick={handleDownloadPastEventsCSV}>
            <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
            {DOWNLOAD_CSV_REPORT_LABEL}
          </MenuItem>
          <MenuItem
            data-testid={DOWNLOAD_CSV_BY_US_STATE_BUTTON_TEST_ID}
            onClick={handleDownloadPastEventsByUSStateCsv}
          >
            <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
            {DOWNLOAD_CSV_REPORT_BY_US_STATE_LABEL}
          </MenuItem>
        </DropdownMenu>
      }
    >
      <FilterableTable
        data={data}
        rowActions={
          <ActionsMenu>
            <ActionReportIssues onAfterSubmit={fetchData} displaySeparately />
            <ActionReportNoIssues onAfterSubmit={fetchData} displaySeparately />
          </ActionsMenu>
        }
        tableId={TABLE_ID}
        loading={isLoading}
        fetching={isFetching}
        pagination={pagination}
        filters={pastEventsFilters(data)}
        columns={PAST_EVENTS_COLUMNS}
        selectedFilters={selectedFilters}
        onPaginationChange={setPagination}
        onFiltersChange={setFilters}
        noDataMessage={NO_DATA_TEXT}
      />
    </DataCard>
  )
}

export default PastEventsView
